import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Mail, Clock, CheckCircle, Play, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showProgress, setShowProgress] = useState(false);
  const [jobId, setJobId] = useState<string | null>(() => localStorage.getItem('processing_job_id'));
  const [progress, setProgress] = useState({ processed: 0, total: 0, status: 'running', current_email: '' });
  const [maxEmails, setMaxEmails] = useState(50);
  const [monitorStatus, setMonitorStatus] = useState({ running: false, poll_interval: 60 });

  // Fetch monitor status
  const { data: monitorData } = useQuery({
    queryKey: ['monitor-status'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/emails/monitor/status`);
      setMonitorStatus(response.data);
      return response.data;
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Check for existing job on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem('processing_job_id');
    if (savedJobId) {
      setJobId(savedJobId);
      // Fetch current status immediately
      axios.get(`${API_BASE_URL}/emails/processing/${savedJobId}`)
        .then(response => {
          setProgress(response.data);
          setShowProgress(true);
        })
        .catch(error => {
          console.error('Error fetching initial status:', error);
          localStorage.removeItem('processing_job_id');
          setShowProgress(false);
        });
    }
  }, []);

  const { data: stats } = useQuery({
    queryKey: ['review-stats'],
    queryFn: async () => {
      const response = await reviewAPI.getStats();
      return response.data;
    },
    refetchInterval: monitorStatus.running ? 10000 : false, // Refresh every 10 seconds when monitoring
  });

  // Poll for progress updates
  useEffect(() => {
    if (!jobId || (progress.status !== 'running' && progress.status !== '')) return;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/emails/processing/${jobId}`);
        setProgress(response.data);
        
        // Refresh stats while processing to show real-time updates
        if (response.data.status === 'running') {
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
        }
        
        if (response.data.status === 'completed') {
          toast.success(`Processed ${response.data.processed} emails successfully`);
          queryClient.invalidateQueries({ queryKey: ['review-stats'] });
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 3000); // Hide after 3 seconds
        } else if (response.data.status === 'failed') {
          toast.error('Failed to process emails');
          localStorage.removeItem('processing_job_id');
        } else if (response.data.status === 'cancelled') {
          localStorage.removeItem('processing_job_id');
          setTimeout(() => {
            setShowProgress(false);
            setJobId(null);
          }, 2000);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    }, 1000); // Poll every second

    return () => clearInterval(interval);
  }, [jobId, progress.status, queryClient]);

  const handleProcessEmails = async () => {
    try {
      const response = await emailAPI.process({ max_emails: maxEmails });
      const newJobId = response.data.job_id;
      setJobId(newJobId);
      localStorage.setItem('processing_job_id', newJobId);
      setProgress({ processed: 0, total: response.data.max_emails, status: 'running', current_email: '' });
      setShowProgress(true);
    } catch (error) {
      toast.error('Failed to start processing');
    }
  };

  const handleToggleMonitor = async () => {
    try {
      if (monitorStatus.running) {
        await axios.post(`${API_BASE_URL}/emails/monitor/stop`);
        toast.success('Auto-monitoring stopped');
      } else {
        await axios.post(`${API_BASE_URL}/emails/monitor/start`);
        toast.success('Auto-monitoring started');
      }
      queryClient.invalidateQueries({ queryKey: ['monitor-status'] });
    } catch (error) {
      toast.error('Failed to toggle monitor');
    }
  };

  const handleCancelProcessing = async () => {
    if (!jobId) return;
    
    try {
      await axios.post(`${API_BASE_URL}/emails/processing/${jobId}/cancel`);
      toast.success('Processing cancelled');
      localStorage.removeItem('processing_job_id');
      setShowProgress(false);
      setJobId(null);
    } catch (error) {
      toast.error('Failed to cancel processing');
    }
  };

  const statCards = [
    {
      title: 'Pending Review',
      value: stats?.pending_review || 0,
      description: 'Emails waiting for review',
      onClick: () => navigate('/review'),
    },
    {
      title: 'Approved',
      value: stats?.approved || 0,
      description: 'Ready to send',
      onClick: () => navigate('/approved'),
    },
    {
      title: 'Sent',
      value: stats?.sent || 0,
      description: 'Successfully sent emails',
      onClick: () => navigate('/emails?status=sent'),
    },
    {
      title: 'Rejected',
      value: stats?.rejected || 0,
      description: 'Rejected emails',
      onClick: () => navigate('/emails?status=rejected'),
    },
    {
      title: 'Total Processed',
      value: stats?.total || 0,
      description: 'All time emails processed',
      onClick: () => navigate('/emails'),
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Overview of your email automation system
        </p>
      </div>

      {/* Process Emails Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Process New Emails</h3>
                <p className="text-sm text-muted-foreground">
                  Fetch and categorize new emails
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">
                    Max emails:
                  </label>
                  <input
                    type="number"
                    value={maxEmails}
                    onChange={(e) => setMaxEmails(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="1000"
                    className="w-20 px-3 py-1.5 text-sm border border-input rounded-md focus:outline-none"
                    disabled={showProgress && progress.status === 'running'}
                  />
                </div>
                <Button
                  onClick={handleProcessEmails}
                  disabled={showProgress && progress.status === 'running'}
                  variant="outline"
                >
                  {showProgress && progress.status === 'running' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Process Emails
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress Section */}
            {showProgress && progress.total > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">
                        {progress.processed} / {progress.total}
                      </span>
                      {progress.status === 'running' && (
                        <Button
                          onClick={handleCancelProcessing}
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                  <Progress 
                    value={progress.total > 0 ? (progress.processed / progress.total) * 100 : 0} 
                  />
                </div>
                {progress.status === 'running' && progress.processed === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Fetching {progress.total} emails from Gmail...</span>
                  </div>
                )}
                {progress.current_email && progress.status === 'running' && progress.processed > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span className="truncate">{progress.current_email}</span>
                  </div>
                )}
                {progress.status === 'completed' && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>All emails processed successfully</span>
                  </div>
                )}
                {progress.status === 'failed' && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <span>Processing failed</span>
                  </div>
                )}
                {progress.status === 'cancelled' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Processing cancelled</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Auto-Monitor Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Auto-Monitor</h3>
              <p className="text-sm text-muted-foreground">
                Automatically check for new emails every {monitorStatus.poll_interval} seconds
              </p>
            </div>
            <Button
              onClick={handleToggleMonitor}
              variant="outline"
              className={monitorStatus.running ? 'border-green-200 text-green-700 hover:bg-green-50' : ''}
            >
              {monitorStatus.running ? 'Stop Monitoring' : 'Start Monitoring'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
        {statCards.map((stat) => {
          return (
            <Card
              key={stat.title}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={stat.onClick}
            >
              <CardHeader>
                <CardDescription className="text-xs uppercase tracking-wide font-medium">
                  {stat.title}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <p className="text-sm text-muted-foreground">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and navigation
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Button
            onClick={() => navigate('/review')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left">
              <div className="font-semibold">Review Queue</div>
              <div className="text-sm text-muted-foreground font-normal">
                Review and approve pending emails
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/approved')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left">
              <div className="font-semibold">Send Emails</div>
              <div className="text-sm text-muted-foreground font-normal">
                Send approved emails to customers
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/settings')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left">
              <div className="font-semibold">Settings</div>
              <div className="text-sm text-muted-foreground font-normal">
                Configure prompts and API settings
              </div>
            </div>
          </Button>
          <Button
            onClick={() => navigate('/emails')}
            className="h-auto py-4 justify-start"
            variant="outline"
          >
            <div className="text-left">
              <div className="font-semibold">All Emails</div>
              <div className="text-sm text-muted-foreground font-normal">
                View complete email history
              </div>
            </div>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
